import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get } from '@/lib/api-client';
import type {
  AnalyticsFilters,
  PaginationParams,
  SlaComplianceResult,
  TurnaroundResult,
  AtRiskResult,
  WorkloadResult,
  ChartingEfficiencyResult,
  BacklogTrendResult,
  RequestVolumeResult,
  AeTurnaroundResult,
  AeEffectivenessResult,
  ExceptionsResult,
  ExpiringHoldsResult,
  VolumeBreakdownResult,
} from '@/types';

const ANALYTICS_STALE_TIME = 2 * 60 * 1000; // 2 minutes
const ANALYTICS_BASE_URL = '/admin/analytics';

/**
 * Build query params from filters and optional pagination
 */
function buildParams(
  filters: AnalyticsFilters,
  pagination?: Partial<PaginationParams>
): Record<string, string> {
  const params: Record<string, string> = {
    startDate: filters.startDate,
    endDate: filters.endDate,
  };

  if (filters.region && filters.region.length > 0) {
    params.region = filters.region.join(',');
  }

  // Only include pagination params when they indicate active drill-down usage.
  // The backend skips server-side caching when any pagination param is present
  // (hasPaginationParams check in analytics-routes), so we only send params
  // when the user has navigated beyond defaults — indicating they're actively
  // using the drill-down table. We also treat a non-default sortBy as
  // drill-down so the user's column sort selection always takes effect.
  if (pagination) {
    const isDrillDown = (pagination.page && pagination.page !== 1) ||
                        (pagination.pageSize && pagination.pageSize !== 25) ||
                        (pagination.sortDir && pagination.sortDir !== 'desc') ||
                        !!pagination.sortBy;

    if (isDrillDown) {
      if (pagination.page) params.page = String(pagination.page);
      if (pagination.pageSize) params.pageSize = String(pagination.pageSize);
      if (pagination.sortBy) params.sortBy = pagination.sortBy;
      if (pagination.sortDir) params.sortDir = pagination.sortDir;
    }
  }

  return params;
}

// --- AdOps Metric Hooks ---

/**
 * Hook for SLA Compliance Rate (Metric 1.1)
 */
export function useSlaCompliance(
  filters: AnalyticsFilters,
  pagination?: Partial<PaginationParams>
) {
  return useQuery({
    queryKey: ['analytics', 'sla-compliance', filters, pagination],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: SlaComplianceResult }>(
        `${ANALYTICS_BASE_URL}/sla-compliance`,
        { params: buildParams(filters, pagination) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for Average Turnaround Time (Metric 1.2)
 */
export function useTurnaround(filters: AnalyticsFilters) {
  return useQuery({
    queryKey: ['analytics', 'turnaround', filters],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: TurnaroundResult }>(
        `${ANALYTICS_BASE_URL}/turnaround`,
        { params: buildParams(filters) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for At-Risk / Overdue Line Items (Metric 1.3)
 */
export function useAtRiskHolds(
  filters: AnalyticsFilters,
  pagination?: Partial<PaginationParams>
) {
  return useQuery({
    queryKey: ['analytics', 'at-risk-holds', filters, pagination],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: AtRiskResult }>(
        `${ANALYTICS_BASE_URL}/at-risk-holds`,
        { params: buildParams(filters, pagination) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for Charting Team Workload Distribution (Metric 1.4)
 */
export function useWorkload(filters: AnalyticsFilters) {
  return useQuery({
    queryKey: ['analytics', 'workload', filters],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: WorkloadResult }>(
        `${ANALYTICS_BASE_URL}/workload`,
        { params: buildParams(filters) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for Charting Member Efficiency (Metric 1.5)
 */
export function useChartingEfficiency(
  filters: AnalyticsFilters,
  pagination?: Partial<PaginationParams>
) {
  return useQuery({
    queryKey: ['analytics', 'charting-efficiency', filters, pagination],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: ChartingEfficiencyResult }>(
        `${ANALYTICS_BASE_URL}/charting-efficiency`,
        { params: buildParams(filters, pagination) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for Unassigned Backlog Trend (Metric 1.6)
 */
export function useBacklogTrend(filters: AnalyticsFilters) {
  return useQuery({
    queryKey: ['analytics', 'backlog-trend', filters],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: BacklogTrendResult }>(
        `${ANALYTICS_BASE_URL}/backlog-trend`,
        { params: buildParams(filters) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

// --- Sales Metric Hooks ---

/**
 * Hook for Request Volume Trend (Metric 2.1)
 */
export function useRequestVolume(filters: AnalyticsFilters) {
  return useQuery({
    queryKey: ['analytics', 'request-volume', filters],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: RequestVolumeResult }>(
        `${ANALYTICS_BASE_URL}/request-volume`,
        { params: buildParams(filters) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for AE Turnaround Time (Metric 2.2)
 */
export function useAeTurnaround(filters: AnalyticsFilters) {
  return useQuery({
    queryKey: ['analytics', 'ae-turnaround', filters],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: AeTurnaroundResult }>(
        `${ANALYTICS_BASE_URL}/ae-turnaround`,
        { params: buildParams(filters) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for AE Usage & Effectiveness (Metric 2.3)
 */
export function useAeEffectiveness(
  filters: AnalyticsFilters,
  pagination?: Partial<PaginationParams>
) {
  return useQuery({
    queryKey: ['analytics', 'ae-effectiveness', filters, pagination],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: AeEffectivenessResult }>(
        `${ANALYTICS_BASE_URL}/ae-effectiveness`,
        { params: buildParams(filters, pagination) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for Exception Rate & Outcomes (Metric 2.4)
 */
export function useExceptions(
  filters: AnalyticsFilters,
  pagination?: Partial<PaginationParams>
) {
  return useQuery({
    queryKey: ['analytics', 'exceptions', filters, pagination],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: ExceptionsResult }>(
        `${ANALYTICS_BASE_URL}/exceptions`,
        { params: buildParams(filters, pagination) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for Expiring Holds / Revenue Risk (Metric 2.5)
 */
export function useExpiringHolds(
  filters: AnalyticsFilters,
  pagination?: Partial<PaginationParams>
) {
  return useQuery({
    queryKey: ['analytics', 'expiring-holds', filters, pagination],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: ExpiringHoldsResult }>(
        `${ANALYTICS_BASE_URL}/expiring-holds`,
        { params: buildParams(filters, pagination) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * Hook for Region & Category Breakdown (Metric 2.6)
 */
export function useVolumeBreakdown(filters: AnalyticsFilters) {
  return useQuery({
    queryKey: ['analytics', 'volume-breakdown', filters],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: VolumeBreakdownResult }>(
        `${ANALYTICS_BASE_URL}/volume-breakdown`,
        { params: buildParams(filters) }
      );
      return response.data;
    },
    staleTime: ANALYTICS_STALE_TIME,
  });
}

// --- Utility ---

/**
 * Hook to invalidate all analytics query cache (for manual refresh button)
 */
export function useInvalidateAnalytics() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
  };
}
